import { Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
// Helpers
import { getEventAffiliateEarnings } from 'helpers/ticketEvents';
// Ui
import Text from 'ui3/Text/Text';
import Switch from 'ui3/Switch/Switch';
import Input from 'ui3/Input/Input';
// Styles
import styles from './AffiliateProgramSection.module.scss';

export type AffiliateFormFields = {
  affiliateEnabled: boolean;
  affiliateCommissionPercent: string;
};

type AffiliateProgramSectionProps = {
  /** Organizer's share of one PPV sale after the platform fee; the commission base. */
  netProfit: number;
  /** Free streams have nothing to share, so the program cannot be turned on. */
  disabled?: boolean;
  /** A ticketed event's program, which also pays on its tickets. */
  variant?: 'stream' | 'event';
  /** The tier prices a ticket sale pays commission on; null when nothing sells tickets. */
  ticketPrices?: number[] | null;
};

const AffiliateProgramSection = ({
  netProfit,
  disabled = false,
  variant = 'stream',
  ticketPrices = null,
}: AffiliateProgramSectionProps): JSX.Element => {
  const {
    register,
    watch,
    errors,
    trigger,
  } = useFormContext<AffiliateFormFields>();

  const isEvent = variant === 'event';
  const isEnabled = Boolean(watch('affiliateEnabled')) && !disabled;
  const commissionPercent = Number(watch('affiliateCommissionPercent'));
  const earnings = getEventAffiliateEarnings({
    ppvNetProfit: netProfit,
    ticketPrices,
    commissionPercent,
  });

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.copy}>
          <Text variant="body1Medium16">Affiliate Program</Text>
          {isEvent ? (
            <Text variant="body2Regular" color="lights-low">
              Offer Participants a percentage of each sale they generate through
              their unique affiliate link. One commission rate applies to every
              Participant in this event.
            </Text>
          ) : (
            <Text variant="body2Regular" color="lights-low">
              Offer Participants a percentage of each PPV sale they generate
              through their unique affiliate link.
              {isEnabled &&
                ' One commission rate applies to every Participant in this event.'}
            </Text>
          )}
        </div>
        <Switch
          wrapperClassName={styles.switch}
          name="affiliateEnabled"
          ref={register}
          disabled={disabled}
        />
      </div>

      {isEnabled && (
        <div className={styles.commissionField}>
          <Input
            name="affiliateCommissionPercent"
            label="Affiliate Commission"
            aria-label="Affiliate Commission"
            placeholder="10"
            inputMode="decimal"
            ref={register}
            onChange={() => trigger('affiliateCommissionPercent')}
            error={errors?.affiliateCommissionPercent?.message}
            icon="percent"
          />
          <Text as="span" variant="captionRegular" color="lights-low">
            Participant earns{' '}
            {earnings.map(({ amount, per }, index) => (
              <Fragment key={per}>
                {index > 0 && ' and '}
                <Text as="span" variant="captionRegular" color="accent-default">
                  {amount}
                </Text>{' '}
                per {per}
              </Fragment>
            ))}
            .
          </Text>
        </div>
      )}
    </div>
  );
};

export default AffiliateProgramSection;
